import { useEffect, useMemo, useState } from "react";
import { fetchQuestionComments } from "../../../services/testExecutionAPI";
import { TEST_EXECUTION_STATUS_BADGE as STATUS_BADGE } from "../../../utils/testExecutionStatus";

export { STATUS_BADGE };

/**
 * Shared "guts" for the submission review flow used by both the tester and the
 * reviewer pages: loads per-question comments and derives the read-only view
 * data (visible sections, grouped test results, scenario, badge, etc.).
 */
export default function useSubmissionReview(report) {
  const [commentsByQuestion, setCommentsByQuestion] = useState({});

  useEffect(() => {
    if (!report?.id) return;
    fetchQuestionComments(report.id)
      .then((list) => {
        const map = {};
        (list || []).forEach((c) => {
          (map[c.questionId] = map[c.questionId] || []).push(c);
        });
        setCommentsByQuestion(map);
      })
      .catch((err) => console.error("Could not load question comments:", err));
  }, [report?.id]);

  const addComment = (questionId, saved) =>
    setCommentsByQuestion((prev) => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), saved],
    }));

  const removeComment = (questionId, commentId) =>
    setCommentsByQuestion((prev) => ({
      ...prev,
      [questionId]: (prev[questionId] || []).filter((c) => c.id !== commentId),
    }));

  const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

  const getTestResultPosition = (test, sections) => {
    if (!test || !Array.isArray(sections) || sections.length === 0) return null;
    const lookupValues = [
      test.testCase,
      test.questionTitle,
      test.questionText,
      test.caseName,
      test.name,
      test.questionId,
      test.id,
    ]
      .map((value) => normalizeText(value))
      .filter(Boolean);

    if (lookupValues.length === 0) return null;

    for (const [sectionIndex, section] of sections
      .slice()
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      .entries()) {
      const questions = (section.questions || [])
        .slice()
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

      for (const [questionIndex, question] of questions.entries()) {
        const questionValues = [
          question.questionText,
          question.questionTitle,
          question.testCaseName,
          question.id,
        ]
          .map((value) => normalizeText(value))
          .filter(Boolean);

        if (
          questionValues.some((value) => lookupValues.includes(value)) ||
          (normalizeText(section.title) === normalizeText(test.testSuite) &&
            questionValues.some((value) => lookupValues.includes(value)))
        ) {
          return sectionIndex * 100000 + questionIndex;
        }
      }
    }

    return null;
  };

  const orderedTestResults = useMemo(() => {
    const list = [...(report?.testResults || [])];
    if (!report?.questionSections?.length) return list;

    return list.sort((a, b) => {
      const aPos = getTestResultPosition(a, report.questionSections);
      const bPos = getTestResultPosition(b, report.questionSections);
      if (aPos == null && bPos == null) return 0;
      if (aPos == null) return 1;
      if (bPos == null) return -1;
      return aPos - bPos;
    });
  }, [report]);

  const failedResults = useMemo(
    () => orderedTestResults.filter((t) => t.status === "FAILED"),
    [orderedTestResults]
  );

  const groupedTests = useMemo(
    () =>
      orderedTestResults.reduce((acc, t) => {
        (acc[t.testSuite] = acc[t.testSuite] || []).push(t);
        return acc;
      }, {}),
    [orderedTestResults]
  );

  // A standard run has test results; a manual 
  // self-declaration has none. Show the same sections the tester saw.
  const reviewScenario =
    report?.testResults && report.testResults.length > 0 ? "webapp" : "local";

  const isSectionVisible = (section) => {
    const s = section.applicableScenario || "ALL";
    if (s === "ALL") return true;
    if (reviewScenario === "local") return s === "LOCAL_ONLY";
    return s === "WEBAPP_ONLY";
  };

  const sections = useMemo(
    () =>
      (report?.questionSections || [])
        .filter(isSectionVisible)
        .slice()
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report, reviewScenario]
  );

  const unexpectedCount = sections
    .flatMap((s) => s.questions || [])
    .filter((q) => q.isUnexpected).length;

  const badge = report
    ? STATUS_BADGE[report.status] || { type: "neutral", label: report.status }
    : null;

  const toMillis = (dateString) => (dateString ? new Date(dateString).getTime() : null);

  // A thread (question or test result) is unanswered when the reviewer's latest comment has no
  // tester reply at or after it — an earlier reply doesn't count once the reviewer has commented
  // again. When timestamps can't be compared (missing on either side), falls back to "any tester
  // reply counts", matching the previous, looser behaviour.
  const isThreadUnanswered = (comments) => {
    const reviewerTimes = comments.filter((c) => c.authorRole !== "TESTER").map((c) => toMillis(c.createdDate));
    if (reviewerTimes.length === 0) return false;

    const knownReviewerTimes = reviewerTimes.filter((t) => t != null);
    const latestReviewerTime = knownReviewerTimes.length > 0 ? Math.max(...knownReviewerTimes) : null;

    const testerTimes = comments.filter((c) => c.authorRole === "TESTER").map((c) => toMillis(c.createdDate));
    return !testerTimes.some((t) => latestReviewerTime == null || t == null || t >= latestReviewerTime);
  };

  // Questions the reviewer commented on that the tester hasn't answered the latest comment for.
  const flaggedUnrespondedQuestions = sections.flatMap((s) =>
    (s.questions || [])
      .filter((q) => isThreadUnanswered(commentsByQuestion[q.id] || []))
      .map((q) => ({ section: s.title, question: q.questionText || q.questionTitle || `Fråga ${q.id}` }))
  );

  // Test results the reviewer commented on that the tester hasn't answered the latest comment
  // for — must be checked alongside the self-declaration questions above, not instead of them.
  const flaggedUnrespondedTests = Object.values(groupedTests)
    .flat()
    .filter((t) => isThreadUnanswered(t.comments || []))
    .map((t) => ({ section: t.testSuite, question: t.testCase }));

  const flaggedUnresponded = [...flaggedUnrespondedQuestions, ...flaggedUnrespondedTests];

  return {
    commentsByQuestion,
    addComment,
    removeComment,
    failedResults,
    groupedTests,
    reviewScenario,
    sections,
    unexpectedCount,
    badge,
    flaggedUnresponded,
  };
}
